import React, { useMemo, useState } from 'react';
import { Card } from './components/Card';
import { addConsoleUrlToOutput, generateConsoleUrl } from '../cli/consoleBridge';
import { auth } from '../../firebase/init';

interface WorkflowRunnerProps {
  onNavigateBack: () => void;
}

interface WorkflowDefinition {
  id: string;
  label: string;
  command: string;
  description: string;
}

const WORKFLOWS: WorkflowDefinition[] = [
  {
    id: 'email-evidence-pipeline',
    label: 'Email Evidence Pipeline',
    command: 'node scripts/xable.mjs run email-evidence-pipeline --help',
    description: 'Run deterministic email evidence pipeline end-to-end.',
  },
  {
    id: 'verify-receipt',
    label: 'Verify Receipt',
    command: 'node scripts/xable.mjs run verify-receipt --help',
    description: 'Verify exported governance receipt artifacts.',
  },
  {
    id: 'verify-case-package',
    label: 'Verify Case Package',
    command: 'node scripts/xable.mjs run verify-case-package --help',
    description: 'Verify deterministic case package artifacts.',
  },
];

export const WorkflowRunner: React.FC<WorkflowRunnerProps> = ({ onNavigateBack }) => {
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>(WORKFLOWS[0].id);
  const [artifactId, setArtifactId] = useState('');
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [runOutput, setRunOutput] = useState<{ command: string; exitCode: number | null; stdout: string; stderr: string } | null>(null);

  const selectedWorkflow = useMemo(
    () => WORKFLOWS.find((workflow) => workflow.id === selectedWorkflowId) ?? WORKFLOWS[0],
    [selectedWorkflowId]
  );

  const consoleUrl = useMemo(
    () => generateConsoleUrl({ artifactId: artifactId.trim() || undefined }),
    [artifactId]
  );

  const consoleOutput = useMemo(
    () => addConsoleUrlToOutput({ artifactId: artifactId.trim() || undefined }, 'Open workflow output in TrustGate Console:'),
    [artifactId]
  );

  const handleRunWorkflow = async () => {
    setRunning(true);
    setRunError(null);
    setRunOutput(null);

    try {
      const user = auth?.currentUser;
      if (!user) {
        setRunError('Authentication required. Sign in with an admin account to run workflows.');
        return;
      }

      const idToken = await user.getIdToken();
      if (!idToken) {
        setRunError('Unable to acquire auth token. Re-authenticate and retry.');
        return;
      }

      const response = await fetch('/api/workflow-runner/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ workflowId: selectedWorkflow.id }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        setRunError(data?.message || data?.code || 'Workflow execution failed.');
        return;
      }

      setRunOutput({
        command: data.command || selectedWorkflow.command,
        exitCode: typeof data.exitCode === 'number' ? data.exitCode : null,
        stdout: typeof data.stdout === 'string' ? data.stdout : '',
        stderr: typeof data.stderr === 'string' ? data.stderr : '',
      });
    } catch (error) {
      setRunError(error instanceof Error ? error.message : 'Network error while executing workflow.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#f8fafc]">Workflow Runner</h1>
        <button
          onClick={onNavigateBack}
          className="px-4 py-2 text-[#cbd5e1] hover:text-[#f8fafc] transition-colors"
        >
          ← Back to Console
        </button>
      </div>

      <Card title="Runbook Selection">
        <div className="space-y-3">
          <label className="text-sm text-[#cbd5e1]">Workflow</label>
          <select
            value={selectedWorkflowId}
            onChange={(event) => setSelectedWorkflowId(event.target.value)}
            className="w-full px-3 py-2 bg-[#0a0a0f] border border-white/10 rounded-lg text-[#f8fafc]"
          >
            {WORKFLOWS.map((workflow) => (
              <option key={workflow.id} value={workflow.id}>
                {workflow.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-[#cbd5e1]">{selectedWorkflow.description}</p>
          <div className="bg-[#0a0a0f] border border-white/10 rounded-lg p-3 font-mono text-sm text-[#00d4aa] break-all">
            {selectedWorkflow.command}
          </div>
          <button
            onClick={handleRunWorkflow}
            disabled={running}
            className="px-4 py-2 rounded-lg bg-[#00d4aa] hover:bg-[#00a8cc] disabled:opacity-60 text-white font-medium"
          >
            {running ? 'Running…' : 'Run Workflow'}
          </button>
          {runError && (
            <div className="text-sm text-[#ef4444] bg-[#ef4444]/10 border border-[#ef4444]/30 rounded-lg p-3">
              {runError}
            </div>
          )}
        </div>
      </Card>

      <Card title="Console Bridge Output">
        <div className="space-y-3">
          <label className="text-sm text-[#cbd5e1]">Artifact ID (optional)</label>
          <input
            type="text"
            value={artifactId}
            onChange={(event) => setArtifactId(event.target.value)}
            placeholder="prf_..., req_..., or receipt id"
            className="w-full px-3 py-2 bg-[#0a0a0f] border border-white/10 rounded-lg text-[#f8fafc]"
          />
          <div className="text-sm text-[#cbd5e1]">
            Console URL: <span className="text-[#00d4aa] font-mono break-all">{consoleUrl}</span>
          </div>
          <pre className="bg-[#0a0a0f] border border-white/10 rounded-lg p-3 text-xs text-[#cbd5e1] whitespace-pre-wrap">
            {consoleOutput}
          </pre>
        </div>
      </Card>

      <Card className="bg-white/5">
        <div className="text-sm text-[#cbd5e1] space-y-2">
          <p>Execution remains CLI-first. This view provides deterministic command runbooks and console link generation.</p>
          <p>Fail-closed rule: if command input is missing or invalid, do not execute and surface the failure explicitly.</p>
        </div>
      </Card>

      {runOutput && (
        <Card title="Execution Output">
          <div className="space-y-3 text-sm">
            <div className="text-[#cbd5e1]">
              Command: <span className="font-mono text-[#00d4aa] break-all">{runOutput.command}</span>
            </div>
            <div className="text-[#cbd5e1]">Exit Code: {runOutput.exitCode ?? '—'}</div>
            <div>
              <div className="text-[#cbd5e1] mb-1">stdout</div>
              <pre className="bg-[#0a0a0f] border border-white/10 rounded-lg p-3 text-xs text-[#f8fafc] whitespace-pre-wrap max-h-64 overflow-auto">{runOutput.stdout || '—'}</pre>
            </div>
            <div>
              <div className="text-[#cbd5e1] mb-1">stderr</div>
              <pre className="bg-[#0a0a0f] border border-white/10 rounded-lg p-3 text-xs text-[#f8fafc] whitespace-pre-wrap max-h-64 overflow-auto">{runOutput.stderr || '—'}</pre>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
